import React, {FunctionComponent, ReactNode, useMemo} from 'react';
import {inputBaseClasses, useTheme} from '@mui/material';
import Search from '@mui/icons-material/Search';
import {useRouteMatch} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import CommandSearchPalette, {createGlobalScope, useCommandSearchScope, type CommandSearchScope} from '../../../panel-command-search';
import {
    createCommandSearchCategoryLabels,
    createCommandSearchCategoryOrder,
    OPERATOR_COMMAND_SEARCH_CATEGORY_LAYOUTS,
    resolveCommandSearchResultIcon,
} from '../../../../panel-entries';
import {routes} from '../../../../../core/models/static/routes/app-routes';
import SessionStorageService from '../../../../../core/services/cache/session_storage_service';
import CreateUserOnlineProfileDialog from '../../../dialogs/create-user-online-profile';
import {bootstrapPanelCommandSearch} from './bootstrap';
import {usePanelCommandSearchServices} from './use-panel-command-search-services';
import {AppBarSearchFieldWidthBox, getAppBarSearchFieldInputPaddingSx} from '../search-mode';

bootstrapPanelCommandSearch();

export interface PanelCommandSearchProps {
    noStartActions?: boolean;
    /** When true, wraps the trigger with the shared app-bar width box. */
    stretchToSearchSlot?: boolean;
    /** Mode toggler rendered inside the trigger as endAdornment. */
    triggerEndAdornment?: ReactNode;
}

/** App bar trigger + dialog for the operator panel command search. */
const PanelCommandSearch: FunctionComponent<PanelCommandSearchProps> = ({
    noStartActions = false,
    stretchToSearchSlot = false,
    triggerEndAdornment,
}) => {
    const theme = useTheme();
    const {t} = useTranslation('components', {keyPrefix: 'panel-sections.command-search'});
    const {services, onlineProfileDialog} = usePanelCommandSearchServices();

    const categoryLabels = useMemo(
        () => createCommandSearchCategoryLabels(t, services.industries),
        [t, services.industries],
    );
    const categoryOrder = useMemo(
        () => createCommandSearchCategoryOrder(
            services.industries,
            services.canSeeAutocompleteSuggestions,
            services.canSeeAutocompleteSuggestions
                || services.canAccess('lookup.productInformation')
                || services.canAccess('lookup.lookupProductInformation'),
        ),
        [services],
    );

    const categoryIcons = useMemo(() => {
        const icons: Record<string, ReactNode> = {
            nexus: <Search fontSize="inherit"/>,
        };
        services.industries?.forEach((industry) => {
            icons[`nexus-${industry.id}`] = <Search fontSize="inherit"/>;
        });
        return icons;
    }, [services.industries]);

    const noResultsText = useMemo(() => (query: string) => t((tt) => tt.noResults, {query}), [t]);

    const triggerSx = useMemo(() => ({
        [`& .${inputBaseClasses.root}`]: getAppBarSearchFieldInputPaddingSx(theme),
    }), [theme]);

    const palette = (
        <CommandSearchPalette
            triggerPlaceholder={t((tt) => tt.triggerPlaceholder)}
            dialogPlaceholder={t((tt) => tt.dialogPlaceholder)}
            emptyHint={t((tt) => tt.emptyHint)}
            closeLabel={t((tt) => tt.close)}
            clearRecentsLabel={t((tt) => tt.clearRecents)}
            removeRecentLabel={t((tt) => tt.removeRecent)}
            noResultsText={noResultsText}
            triggerEndAdornment={triggerEndAdornment}
            triggerFullWidth={stretchToSearchSlot}
            triggerSx={stretchToSearchSlot ? triggerSx : undefined}
            options={{
                storageKey: SessionStorageService.keys.panelCommandSearchRecent,
                panelId: 'main',
                categoryOrder,
                categoryLabels,
                categoryIcons,
                categoryLayouts: OPERATOR_COMMAND_SEARCH_CATEGORY_LAYOUTS,
                resolveResultIcon: resolveCommandSearchResultIcon,
                services: services as unknown as Record<string, unknown>,
                maxRecentItems: 8,
            }}
        />
    );

    return (
        <>
            {stretchToSearchSlot ? (
                <AppBarSearchFieldWidthBox noStartActions={noStartActions}>
                    {palette}
                </AppBarSearchFieldWidthBox>
            ) : palette}
            <CreateUserOnlineProfileDialog
                open={onlineProfileDialog.open}
                initialValues={onlineProfileDialog.initialValues}
                onSuccess={onlineProfileDialog.onSuccess}
                setOpen={onlineProfileDialog.onClose}
                onBack={undefined}
            />
        </>
    );
};

export default PanelCommandSearch;

/** Mounts/unmounts customer- and vendor-scoped navigation providers as routes change. */
export const PanelCommandSearchScopeBridge: FunctionComponent = () => {
    const customerMatch = useRouteMatch<{customerId: string}>(routes.customer.base);
    const vendorMatch = useRouteMatch<{vendorId: string}>(routes.vendor.base);

    const scope = useMemo<CommandSearchScope>(() => {
        if (customerMatch) {
            return {panelId: 'main', stack: [{type: 'customer', id: String(customerMatch.params.customerId)}]};
        }
        if (vendorMatch) {
            return {panelId: 'main', stack: [{type: 'vendor', id: String(vendorMatch.params.vendorId)}]};
        }
        return createGlobalScope('main');
    }, [customerMatch, vendorMatch]);

    const mountProviderIds = useMemo(() => {
        if (customerMatch) {
            return ['navigation-customer'];
        }
        if (vendorMatch) {
            return ['navigation-vendor'];
        }
        return [];
    }, [customerMatch, vendorMatch]);

    useCommandSearchScope(scope, {mountProviderIds});

    return null;
};
